import { NavLink, useLocation } from "react-router-dom";
import s from "../styles/Icons.module.css";
import Toggle from "./Theme/Toggle";
import { useDarkMode } from "./Theme/useDarkMode";
import { lightTheme, darkTheme } from "./Theme/Themes";
import { ThemeProvider } from "styled-components";
import { GlobalStyles } from "./Theme/GlobalStyles";
import { useState, useEffect } from "react";
// Material UI Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LinkIcon from "@mui/icons-material/Link";
import ContactsIcon from "@mui/icons-material/Contacts";
import CampaignIcon from "@mui/icons-material/Campaign";
import SettingsIcon from "@mui/icons-material/Settings";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LogoutIcon from "@mui/icons-material/Logout";
import RateReviewIcon from "@mui/icons-material/RateReview";
import DescriptionIcon from "@mui/icons-material/Description";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import BugReportIcon from "@mui/icons-material/BugReport";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import RadarIcon from "@mui/icons-material/Radar";
import SpeedIcon from "@mui/icons-material/Speed";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessIcon from "@mui/icons-material/Business";
import TerminalIcon from "@mui/icons-material/Terminal";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import MapIcon from "@mui/icons-material/Map";
import TimelineIcon from "@mui/icons-material/Timeline";
import GroupsIcon from "@mui/icons-material/Groups";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import HistoryIcon from "@mui/icons-material/History";
import AppsIcon from "@mui/icons-material/Apps";
import TuneIcon from "@mui/icons-material/Tune";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HubIcon from "@mui/icons-material/Hub";
import ArticleIcon from "@mui/icons-material/Article";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ScienceIcon from "@mui/icons-material/Science";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import InsightsIcon from "@mui/icons-material/Insights";
import PhoneIcon from "@mui/icons-material/Phone";

// Sub-paths that belong to the Dev Tools group
const DEV_TOOLS_PATHS = [
  "/dev-tools",
  "/prod-deployment",
  "/roadmap",
  "/evals",
  "/model-switch-eval",
  "/langfuse-analytics",
  "/organization-limits",
  "/gcp-monitoring",
  "/cost-overview",
];

const DEV_TOOLS_CHILDREN = [
  { path: "/dev-tools", label: "Dev Tools", icon: TerminalIcon },
  {
    path: "/prod-deployment",
    label: "Prod Deployment",
    icon: RocketLaunchIcon,
  },
  { path: "/roadmap", label: "Roadmap", icon: MapIcon },
  { path: "/evals", label: "Evals", icon: TimelineIcon },
  {
    path: "/model-switch-eval",
    label: "Evals",
    icon: CompareArrowsIcon,
  },
  {
    path: "/langfuse-analytics",
    label: "AI Usage Analytics",
    icon: AnalyticsIcon,
  },
  {
    path: "/organization-limits",
    label: "Organization Limits",
    icon: TuneIcon,
  },
  {
    path: "/gcp-monitoring",
    label: "GCP Monitoring",
    icon: MonitorHeartIcon,
  },
  {
    path: "/cost-overview",
    label: "Cost Overview",
    icon: AttachMoneyIcon,
  },
];

const Sidebar = ({ onLogout }) => {
  const [theme, themeToggler, mountedComponent] = useDarkMode();
  const themeMode = theme === "light" ? lightTheme : darkTheme;
  const [selectedItem, setSelectedItem] = useState("dashboard");
  const location = useLocation();

  // Dev Tools group open/closed — auto-open when on a child route
  const isOnDevToolsRoute = DEV_TOOLS_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );
  const [devToolsOpen, setDevToolsOpen] = useState(isOnDevToolsRoute);

  // Initialize sidebar state from localStorage or default to false
  const [isExpanded, setIsExpanded] = useState(() => {
    return false;
  });

  useEffect(() => {
    console.log("selectedItem", selectedItem);
  }, [selectedItem]);

  // Auto-expand the group when navigating to a child route
  useEffect(() => {
    if (isOnDevToolsRoute) setDevToolsOpen(true);
  }, [location.pathname, isOnDevToolsRoute]);

  // Add/remove class to body when sidebar expands/collapses
  useEffect(() => {
    if (isExpanded) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }

    return () => {
      document.body.classList.remove("sidebar-expanded");
    };
  }, [isExpanded]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const menuItems = [
    {
      path: "/",
      label: "All Tools",
      icon: AppsIcon,
      key: "home",
      exact: true,
    },
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: DashboardIcon,
      key: "dashboard",
    },
    {
      path: "/briefings",
      label: "Briefings",
      icon: ArticleIcon,
      key: "briefings",
    },
    {
      path: "/project-views",
      label: "Project Views",
      icon: ViewTimelineIcon,
      key: "project-views",
    },
    {
      path: "/org-overview",
      label: "Org 360",
      icon: HubIcon,
      key: "org-overview",
    },
    {
      path: "/integrations",
      label: "Integrations",
      icon: LinkIcon,
      key: "integrations",
    },
    {
      path: "/organization-loan-search",
      label: "Org Loan Search",
      icon: ManageSearchIcon,
      key: "organization-loan-search",
    },
    {
      path: "/pc-watchdog",
      label: "PC Watchdog",
      icon: RadarIcon,
      key: "pc-watchdog",
    },
    {
      path: "/pc-performance",
      label: "PC Performance",
      icon: SpeedIcon,
      key: "pc-performance",
    },
    {
      path: "/marketing",
      label: "Marketing",
      icon: EmailIcon,
      key: "marketing",
    },
    {
      path: "/user-permissions",
      label: "User/Org Permissions",
      icon: PeopleIcon,
      key: "user-permissions",
    },
    {
      path: "/loa-permissions",
      label: "LOA Permissions",
      icon: ManageAccountsIcon,
      key: "loa-permissions",
    },
    {
      path: "/referral-eligibility",
      label: "Referral Eligibility",
      icon: CardGiftcardIcon,
      key: "referral-eligibility",
    },
    /*
    {
      path: "/demo-links",
      label: "Demo Links",
      icon: LinkIcon,
      key: "demo-links",
    },
  */
    {
      path: "/contacts",
      label: "Contacts",
      icon: ContactsIcon,
      key: "contacts",
    },
    {
      path: "/announcements",
      label: "Announcements",
      icon: CampaignIcon,
      key: "announcements",
    },
    {
      path: "/processing-monitoring",
      label: "Processing Monitoring",
      icon: AssessmentIcon,
      key: "processing-monitoring",
    },
    /*
    {
      path: "/custom-instructions",
      label: "Custom Instructions",
      icon: SchoolIcon,
      key: "custom-instructions",
    },
    */
    {
      path: "/lead-conversion",
      label: "Lead Conversion",
      icon: TrendingUpIcon,
      key: "lead-conversion",
    },
    {
      path: "/qa-sprint-analytics",
      label: "QA Sprint Analytics",
      icon: BugReportIcon,
      key: "qa-sprint-analytics",
    },
    {
      path: "/support-insights",
      label: "Support Insights",
      icon: SupportAgentIcon,
      key: "support-insights",
    },
    {
      path: "/org-usage-analytics",
      label: "User Usage",
      icon: GroupsIcon,
      key: "org-usage-analytics",
    },
    {
      path: "/client-tracking",
      label: "Client Tracking",
      icon: InsightsIcon,
      key: "client-tracking",
    },
    {
      path: "/pilots",
      label: "Pilots",
      icon: ScienceIcon,
      key: "pilots",
    },
    {
      path: "/voice-console",
      label: "Voice Agent (test)",
      icon: PhoneIcon,
      key: "voice-console",
    },
    {
      path: "/demo-invites",
      label: "Demo Invites",
      icon: VpnKeyIcon,
      key: "demo-invites",
    },
    {
      path: "/demo-access",
      label: "Demo Access",
      icon: VerifiedUserIcon,
      key: "demo-access",
    },
    {
      path: "/enterprise-orgs",
      label: "Enterprise Orgs",
      icon: BusinessIcon,
      key: "enterprise-orgs",
    },
    {
      path: "/enterprise-billing-usage",
      label: "Enterprise Billing",
      icon: AttachMoneyIcon,
      key: "enterprise-billing-usage",
    },
    {
      path: "/income-calculation-runs",
      label: "Income Runs",
      icon: HistoryIcon,
      key: "income-calculation-runs",
    },
    {
      path: "/newfi-split-edocs",
      label: "Split eDocs",
      icon: ContentCutIcon,
      key: "newfi-split-edocs",
    },
    {
      path: "/org-users-report",
      label: "Track Onboarding",
      icon: GroupsIcon,
      key: "org-users-report",
    },
    {
      path: "/signups",
      label: "Sign-ups by Source",
      icon: HowToRegIcon,
      key: "signups",
    },
    {
      path: "/ask-addy-chats",
      label: "Ask Addy Chats",
      icon: ChatBubbleIcon,
      key: "ask-addy-chats",
    },
    {
      path: "/changelogs",
      label: "Changelogs",
      icon: HistoryIcon,
      key: "changelogs",
    },
    /*
    {
      path: "/prompts",
      label: "Prompts",
      icon: DescriptionIcon,
      key: "prompts",
    },
    */
    {
      path: "/settings",
      label: "Whitelist Management",
      icon: SettingsIcon,
      key: "settings",
    },
    {
      path: "/notifications",
      label: "Notifications",
      icon: NotificationsIcon,
      key: "notifications",
    },
    {
      path: "/queue",
      label: "Queue",
      icon: FormatListBulletedIcon,
      key: "queue",
    },
    {
      path: "/feedback",
      label: "Feedback",
      icon: RateReviewIcon,
      key: "feedback",
    },
  ];

  if (!mountedComponent) return <div />;

  const isDevToolsGroupActive = DEV_TOOLS_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );

  return (
    <div
      className={`${s.sidebar} SidebarContainer ${isExpanded ? "expanded" : "collapsed"}`}
    >
      <ThemeProvider theme={themeMode}>
        <GlobalStyles />
        <div className="SidebarContent">
          {/* Toggle Button */}
          <div className="SidebarToggle" onClick={toggleSidebar}>
            {isExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </div>

          {/* Navigation Items */}
          <div className="SidebarContentNavItems">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const isSelected = selectedItem === item.key;

              return (
                <Tooltip
                  key={item.key}
                  title={item.label}
                  placement="right"
                  arrow
                  disableHoverListener={isExpanded}
                >
                  <div
                    className={`SidebarContentNavItem ${isSelected ? "selected" : ""}`}
                    onClick={() => setSelectedItem(item.key)}
                  >
                    <NavLink
                      to={item.path}
                      exact={item.exact}
                      activeClassName={s.active}
                    >
                      <div className="nav-item-content">
                        <IconComponent
                          className="nav-icon"
                          style={{
                            color: isSelected ? "#7F7FD5" : "#DFE6F1",
                            fontSize: "24px",
                          }}
                        />
                        {isExpanded && (
                          <span className="nav-label">{item.label}</span>
                        )}
                      </div>
                    </NavLink>
                  </div>
                </Tooltip>
              );
            })}

            {/* ── Dev Tools group ── */}
            <Tooltip
              title={isExpanded ? "" : "Dev Tools"}
              placement="right"
              arrow
              disableHoverListener={isExpanded}
            >
              <div
                className={`SidebarContentNavItem sidebar-group-header ${isDevToolsGroupActive ? "selected" : ""}`}
                onClick={() => {
                  if (!isExpanded) {
                    // collapsed sidebar: just toggle the group open so sub-items show
                    setDevToolsOpen((v) => !v);
                  } else {
                    setDevToolsOpen((v) => !v);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="nav-item-content" style={{ padding: "12px" }}>
                  <TerminalIcon
                    className="nav-icon"
                    style={{
                      color: isDevToolsGroupActive ? "#7F7FD5" : "#DFE6F1",
                      fontSize: "24px",
                    }}
                  />
                  {isExpanded && (
                    <>
                      <span
                        className="nav-label"
                        style={{
                          flex: 1,
                          color: isDevToolsGroupActive ? "#7F7FD5" : undefined,
                        }}
                      >
                        Dev Tools
                      </span>
                      {devToolsOpen ? (
                        <ExpandLessIcon
                          style={{ color: "#9ca3af", fontSize: 18 }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          style={{ color: "#9ca3af", fontSize: 18 }}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </Tooltip>

            {/* Sub-items — visible when group is open */}
            {devToolsOpen &&
              DEV_TOOLS_CHILDREN.map((child) => {
                const ChildIcon = child.icon;
                const isChildSelected =
                  selectedItem === child.path.replace("/", "");
                return (
                  <Tooltip
                    key={child.path}
                    title={isExpanded ? "" : child.label}
                    placement="right"
                    arrow
                    disableHoverListener={isExpanded}
                  >
                    <div
                      className={`SidebarContentNavItem sidebar-sub-item ${isChildSelected ? "selected" : ""}`}
                      onClick={() =>
                        setSelectedItem(child.path.replace("/", ""))
                      }
                    >
                      <NavLink to={child.path} activeClassName={s.active}>
                        <div className="nav-item-content sidebar-sub-item-content">
                          <ChildIcon
                            className="nav-icon"
                            style={{
                              color: isChildSelected ? "#7F7FD5" : "#a0aec0",
                              fontSize: "20px",
                            }}
                          />
                          {isExpanded && (
                            <span
                              className="nav-label"
                              style={{
                                fontSize: "13px",
                                color: isChildSelected ? "#7F7FD5" : "#a0aec0",
                              }}
                            >
                              {child.label}
                            </span>
                          )}
                        </div>
                      </NavLink>
                    </div>
                  </Tooltip>
                );
              })}
          </div>

          {/* Logout Button */}
          {onLogout && (
            <div className="SidebarLogout">
              <Tooltip
                title="Logout"
                placement="right"
                arrow
                disableHoverListener={isExpanded}
              >
                <div
                  className="SidebarContentNavItem logout-item"
                  onClick={onLogout}
                  style={{ cursor: "pointer" }}
                >
                  <div className="nav-item-content">
                    <LogoutIcon
                      className="nav-icon"
                      style={{
                        color: "#ff6b6b",
                        fontSize: "24px",
                      }}
                    />
                    {isExpanded && (
                      <span className="nav-label" style={{ color: "#ff6b6b" }}>
                        Logout
                      </span>
                    )}
                  </div>
                </div>
              </Tooltip>
            </div>
          )}

          {/* Theme Toggle */}
          <Toggle theme={theme} toggleTheme={themeToggler} />
        </div>
      </ThemeProvider>
    </div>
  );
};

export default Sidebar;
